import { createFileRoute, Link } from "@tanstack/react-router";

const TITLE = "The 4 Stages of Menopause: A Complete Guide | KnowMeno";
const DESCRIPTION =
  "Premenopause, perimenopause, menopause, and postmenopause explained — symptoms, timing, and treatments for every stage of the menopause transition.";
const CANONICAL = "https://knowmeno.com/menopause-guide";
const OG_IMAGE = "https://knowmeno.com/og-image.png?v=2";
const LAST_REVIEWED = new Date().toISOString().slice(0, 10);

const PLUM = "#2D0E43";
const PLUM_DEEP = "#4B1F4F";
const CORAL = "#F34E54";
const INK = "#231235";
const MUTED = "#5C4A66";

const faqItems: Array<{ q: string; a: string }> = [
  {
    q: "What is the difference between premenopause and perimenopause?",
    a: "Premenopause is your entire reproductive span before any menopausal changes — regular cycles and normal fertility. Perimenopause is the transition into menopause, when hormones fluctuate, cycles become irregular, and symptoms like hot flashes begin. Both mean \"before menopause,\" which is why they're confused, but they describe different things.",
  },
  {
    q: "What are the four stages of menopause?",
    a: "Premenopause (your reproductive years before changes), perimenopause (the transition, when symptoms begin), menopause (the single point 12 months after your last period), and postmenopause (the rest of your life after that).",
  },
  {
    q: "How long does perimenopause last?",
    a: "Usually about 4 to 8 years before your final period, but the range is wide — from a few months to over a decade.",
  },
  {
    q: "What age does menopause usually start?",
    a: "The average age is around 51 to 52, though anywhere in your 40s or 50s is normal. Menopause before 40 is considered premature and deserves specialized care.",
  },
  {
    q: "Can you still get pregnant during perimenopause?",
    a: "Yes. Ovulation becomes less predictable and fertility declines, but pregnancy is still possible until you've fully reached menopause (12 consecutive months without a period).",
  },
  {
    q: "How do I know if I've reached menopause?",
    a: "Menopause is confirmed once you've gone 12 consecutive months without a menstrual period, with no other medical cause. For most women it's diagnosed clinically, without routine hormone testing.",
  },
  {
    q: "What are the long-term health risks after menopause?",
    a: "Lower estrogen accelerates bone loss (raising osteoporosis and fracture risk) and increases cardiovascular risk. Heart disease is the leading cause of death in women, so bone density screening and heart health become priorities in postmenopause.",
  },
  {
    q: "What non-hormonal treatments are available for hot flashes?",
    a: "Options include fezolinetant (Veozah) and the newer elinzanetant (Lynkuet), both designed specifically for hot flashes, plus off-label medications such as certain antidepressants, gabapentin, and oxybutynin. Lifestyle changes also help.",
  },
];

export const Route = createFileRoute("/menopause-guide")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { name: "robots", content: "index, follow, max-image-preview:large" },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: CANONICAL },
      { property: "og:site_name", content: "KnowMeno" },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: OG_IMAGE },
    ],
    links: [
      { rel: "canonical", href: CANONICAL },
      { rel: "alternate", hrefLang: "en", href: CANONICAL },
      { rel: "alternate", hrefLang: "x-default", href: CANONICAL },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalWebPage",
          name: TITLE,
          description: DESCRIPTION,
          url: CANONICAL,
          inLanguage: "en",
          lastReviewed: LAST_REVIEWED,
          publisher: { "@type": "Organization", name: "KnowMeno" },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqItems.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://knowmeno.com/" },
            { "@type": "ListItem", position: 2, name: "Menopause Guide", item: CANONICAL },
          ],
        }),
      },
    ],
  }),
  component: MenopauseGuidePage,
});

const h2Style: React.CSSProperties = {
  fontFamily: "'Gloock', serif",
  fontSize: "clamp(1.6rem, 3.5vw, 2.1rem)",
  color: PLUM,
  marginTop: 56,
  marginBottom: 16,
  lineHeight: 1.2,
  scrollMarginTop: 24,
};

const h3Style: React.CSSProperties = {
  fontFamily: "'Gloock', serif",
  fontSize: "clamp(1.15rem, 2.4vw, 1.35rem)",
  color: PLUM_DEEP,
  marginTop: 28,
  marginBottom: 10,
  lineHeight: 1.3,
};

const pStyle: React.CSSProperties = {
  fontFamily: "'Lora', Georgia, serif",
  fontSize: "1.0625rem",
  lineHeight: 1.75,
  color: INK,
  margin: "0 0 16px",
};

const ulStyle: React.CSSProperties = {
  fontFamily: "'Lora', Georgia, serif",
  fontSize: "1.0625rem",
  lineHeight: 1.75,
  color: INK,
  paddingLeft: "1.25rem",
  margin: "0 0 16px",
};

const internalLinkStyle: React.CSSProperties = {
  // Darker coral variant to ensure WCAG AA 4.5:1 contrast for inline link text on cream background.
  color: "#B82A30",
  textDecoration: "underline",
  textUnderlineOffset: 2,
  fontWeight: 600,
};

function MenopauseGuidePage() {
  return (
    <div
      style={{
        background:
          "radial-gradient(1200px 600px at 10% -10%, rgba(243,78,84,0.07), transparent 60%), radial-gradient(900px 500px at 100% 0%, rgba(255,200,210,0.25), transparent 55%), #FFFBF8",
        minHeight: "100vh",
      }}
    >
      {/* Top bar */}
      <header
        style={{
          borderBottom: "1px solid rgba(75,31,79,0.08)",
          background: "rgba(255,255,255,0.85)",
          backdropFilter: "saturate(140%) blur(8px)",
        }}
      >
        <div
          style={{
            maxWidth: 880,
            margin: "0 auto",
            padding: "14px 20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 16,
          }}
        >
          <Link
            to="/"
            style={{
              fontFamily: "'Gloock', serif",
              fontSize: 22,
              color: PLUM,
              textDecoration: "none",
              letterSpacing: 0.2,
            }}
          >
            <span style={{ color: PLUM_DEEP }}>Know</span>
            <span style={{ color: CORAL }}>Meno</span>
          </Link>
          <Link
            to="/sign-up"
            style={{
              fontFamily: "'Lora', serif",
              fontSize: 14,
              background: CORAL,
              color: "#fff",
              padding: "10px 16px",
              borderRadius: 999,
              textDecoration: "none",
              fontWeight: 600,
            }}
          >
            Start tracking free
          </Link>
        </div>
      </header>

      <main
        style={{
          maxWidth: 760,
          margin: "0 auto",
          padding: "32px 20px 80px",
        }}
      >
        <nav
          aria-label="Breadcrumb"
          style={{ fontFamily: "'Lora', serif", fontSize: 14, color: MUTED, marginBottom: 24 }}
        >
          <ol style={{ display: "flex", flexWrap: "wrap", gap: 6, listStyle: "none", padding: 0, margin: 0 }}>
            <li>
              <Link to="/" style={{ color: MUTED, textDecoration: "underline", textUnderlineOffset: 2 }}>
                Home
              </Link>
            </li>
            <li aria-hidden="true">›</li>
            <li aria-current="page" style={{ color: PLUM_DEEP, fontWeight: 600 }}>
              Menopause Guide
            </li>
          </ol>
        </nav>

        <article>
          <h1
            style={{
              fontFamily: "'Gloock', serif",
              fontSize: "clamp(2rem, 5.5vw, 3rem)",
              color: PLUM,
              lineHeight: 1.15,
              margin: "0 0 20px",
            }}
          >
            Understanding Your Menopause Journey: The 4 Stages Explained
          </h1>

          <p style={pStyle}>
            Menopause isn't a single event — it's a transition that unfolds over years, in four
            recognized stages. The two terms women mix up most are premenopause and perimenopause.
            They sound alike and both mean "before menopause," but they describe very different
            things. Here's a clear, honest guide to each stage — what's happening in your body,
            what you might notice, and what you can do about it. This is education, not diagnosis:
            if something here resonates, it's a starting point for a conversation with your doctor.
          </p>

          {/* TOC */}
          <nav
            aria-label="On this page"
            style={{
              background: "rgba(255,255,255,0.7)",
              border: "1px solid rgba(75,31,79,0.12)",
              borderRadius: 14,
              padding: "16px 20px",
              margin: "24px 0 8px",
            }}
          >
            <div
              style={{
                fontFamily: "'Gloock', serif",
                color: PLUM,
                fontSize: 15,
                marginBottom: 10,
                letterSpacing: 0.3,
                textTransform: "uppercase",
              }}
            >
              On this page
            </div>
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: "6px 16px",
                fontFamily: "'Lora', serif",
                fontSize: 15,
              }}
            >
              <li><a href="#premenopause" style={internalLinkStyle}>Premenopause</a></li>
              <li><a href="#perimenopause" style={internalLinkStyle}>Perimenopause</a></li>
              <li><a href="#menopause" style={internalLinkStyle}>Menopause</a></li>
              <li><a href="#postmenopause" style={internalLinkStyle}>Postmenopause</a></li>
              <li><a href="#treatment" style={internalLinkStyle}>Treatment</a></li>
              <li><a href="#faq" style={internalLinkStyle}>FAQ</a></li>
            </ul>
          </nav>

          <h2 style={h2Style}>The 4 Stages of Menopause at a Glance</h2>

          <div style={{ overflowX: "auto", margin: "0 -4px 16px", WebkitOverflowScrolling: "touch" }}>
            <table
              style={{
                borderCollapse: "collapse",
                width: "100%",
                minWidth: 640,
                fontFamily: "'Lora', serif",
                fontSize: 15,
                color: INK,
                background: "rgba(255,255,255,0.7)",
                border: "1px solid rgba(75,31,79,0.12)",
                borderRadius: 12,
                overflow: "hidden",
              }}
            >
              <thead style={{ background: "rgba(75,31,79,0.06)" }}>
                <tr>
                  {["Stage", "What it means", "Typical timing", "Periods", "Can you get pregnant?"].map((h) => (
                    <th
                      key={h}
                      scope="col"
                      style={{
                        textAlign: "left",
                        padding: "12px 14px",
                        fontFamily: "'Gloock', serif",
                        color: PLUM,
                        borderBottom: "1px solid rgba(75,31,79,0.12)",
                        fontWeight: 400,
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ["Premenopause", "Your full reproductive span, before any menopausal changes", "First period through your late 30s–early 40s", "Regular", "Yes — normal fertility"],
                  ["Perimenopause", "The transition into menopause, when hormones fluctuate and symptoms begin", "Usually early 40s to early 50s; lasts ~4–8 years", "Irregular", "Yes — still possible"],
                  ["Menopause", "A single point in time: 12 consecutive months with no period", "Average age ~51–52", "None for 12+ months", "No"],
                  ["Postmenopause", "Everything after that 12-month mark, for the rest of your life", "From menopause onward", "None", "No"],
                ].map((row, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid rgba(75,31,79,0.08)" }}>
                    {row.map((cell, j) => (
                      <td
                        key={j}
                        style={{
                          padding: "12px 14px",
                          verticalAlign: "top",
                          color: j === 0 ? PLUM_DEEP : INK,
                          fontWeight: j === 0 ? 600 : 400,
                        }}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p style={pStyle}>
            A simple way to hold it: premenopause is "before anything changes," perimenopause is
            "the change in progress," menopause is "the finish line of your cycles," and
            postmenopause is "life after." Note on language: "premenopause" is more of a casual
            term than a clinical one — many clinicians focus on perimenopause and postmenopause,
            the stages where symptoms and health risks are actively managed.
          </p>

          {/* Premenopause */}
          <section id="premenopause">
            <h2 style={h2Style}>Stage 1: Premenopause</h2>
            <h3 style={h3Style}>What it is</h3>
            <p style={pStyle}>
              Premenopause is the long stretch of your reproductive years before the menopause
              transition begins — from your first period until perimenopause starts. Your ovaries
              produce estrogen and progesterone in their usual rhythm, your cycles are generally
              regular, and your fertility is at typical levels.
            </p>
            <h3 style={h3Style}>What's happening in your body</h3>
            <p style={pStyle}>
              Hormones cycle predictably each month to support ovulation. Subtle shifts can begin
              quietly in your late 30s or early 40s — a slightly shorter cycle, a heavier or
              lighter period — but these usually don't disrupt daily life and aren't yet
              perimenopause.
            </p>
            <h3 style={h3Style}>Why this stage matters</h3>
            <p style={pStyle}>
              This is the ideal time to establish your baseline.{" "}
              <Link to="/sign-up" style={internalLinkStyle}>
                Tracking your cycles
              </Link>
              , mood, sleep, and energy now means that when subtle changes begin, you'll see them
              clearly instead of wondering whether you're imagining things.
            </p>
            <h3 style={h3Style}>When to talk to a clinician</h3>
            <p style={pStyle}>
              Premenopause is a good time for routine well-woman care. Raise anything unusual for
              you — unusually heavy bleeding, periods that suddenly turn irregular in your 30s, or
              unexplained new symptoms. Symptoms that look like menopause can have other causes,
              such as thyroid issues, which is why a clinician's input matters.
            </p>
          </section>

          {/* Perimenopause */}
          <section id="perimenopause">
            <h2 style={h2Style}>Stage 2: Perimenopause</h2>
            <h3 style={h3Style}>What it is</h3>
            <p style={pStyle}>
              Perimenopause — "around menopause" — is the transitional phase when your body begins
              shifting away from its fertile, cycling pattern. It's the stage most people mean when
              they say they're "going through menopause," because it's when symptoms typically
              begin.
            </p>
            <h3 style={h3Style}>When it happens and how long it lasts</h3>
            <p style={pStyle}>
              It usually begins in your early 40s to early 50s, though it can start as early as
              your mid-30s. It commonly lasts about 4 to 8 years before your final period, but the
              range is wide — some women move through it in months, others over a decade.
            </p>
            <h3 style={h3Style}>What's happening in your body</h3>
            <p style={pStyle}>
              This stage is defined by fluctuation, not steady decline. Your ovaries produce
              estrogen erratically — levels swing high and low, sometimes within one cycle. These
              ups and downs, more than any single low point, drive perimenopausal symptoms.
              Ovulation becomes less predictable and fertility gradually declines, though pregnancy
              is still possible until you've fully reached menopause.
            </p>
            <h3 style={h3Style}>Common signs and symptoms</h3>
            <ul style={ulStyle}>
              <li>Irregular periods — shorter or longer cycles, skipped periods, changes in flow</li>
              <li>Hot flashes — a sudden wave of heat in the face, neck, and chest, sometimes with flushing and sweating</li>
              <li>Night sweats — hot flashes during sleep that disrupt rest</li>
              <li>Sleep problems — trouble falling or staying asleep</li>
              <li>Mood changes — irritability, anxiety, low mood, or feeling more emotionally reactive</li>
              <li>Brain fog — difficulty concentrating and changes in memory, especially verbal recall (research confirms this is real, though function usually stays within a normal range)</li>
              <li>Vaginal dryness and discomfort during sex</li>
              <li>Migraines or worsening headaches, especially around your cycle</li>
              <li>Changes in body composition — weight that's harder to manage and a shift toward midsection fat</li>
              <li>Breast tenderness and changes in libido</li>
            </ul>
            <h3 style={h3Style}>How perimenopause is recognized</h3>
            <p style={pStyle}>
              There's no single definitive test. It's usually identified from your age, symptoms,
              and menstrual pattern together. Blood tests for follicle-stimulating hormone (FSH)
              have real limits here: because hormones fluctuate so much, FSH can read normal even
              during clear perimenopause, so one test rarely tells the whole story (and it's
              unreliable if you're on hormonal contraception). Specialists use the Stages of
              Reproductive Aging Workshop (STRAW+10) framework to stage reproductive aging based on
              cycle changes alongside hormone markers.
            </p>
            <h3 style={h3Style}>Why this stage matters</h3>
            <p style={pStyle}>
              Perimenopause is where the "am I imagining this?" feeling hits hardest — and where{" "}
              <Link to="/sign-up" style={internalLinkStyle}>
                your own data
              </Link>{" "}
              changes the conversation. Walking into an appointment with a clear record of your
              symptoms, their frequency, and how they correlate with your cycle and sleep makes you
              far less likely to be dismissed.
            </p>
          </section>

          {/* Menopause */}
          <section id="menopause">
            <h2 style={h2Style}>Stage 3: Menopause</h2>
            <h3 style={h3Style}>What it is</h3>
            <p style={pStyle}>
              Menopause is a specific moment, not a phase: the point at which you've gone 12
              consecutive months without a menstrual period, with no other medical cause. You only
              know you've reached it looking backward, once that full year has passed.
            </p>
            <h3 style={h3Style}>When it happens</h3>
            <p style={pStyle}>
              The average age is around 51 to 52, though anywhere in your 40s or 50s is normal.
              Menopause before age 40 is considered premature — often due to a medical cause,
              surgery, or primary ovarian insufficiency — and carries its own health considerations
              that deserve specialized care.
            </p>
            <h3 style={h3Style}>What's happening in your body</h3>
            <p style={pStyle}>
              Your ovaries have largely stopped releasing eggs and producing high levels of
              estrogen. After the wild swings of perimenopause, estrogen settles into a
              consistently low level. This lower-but-stable state defines the years ahead.
            </p>
            <h3 style={h3Style}>What you might notice</h3>
            <p style={pStyle}>
              Many perimenopausal symptoms can continue through and beyond this point. Symptoms
              tied to hormonal fluctuation, like breast tenderness or cyclical mood swings, often
              ease once hormones stabilize. The upsides: no more periods, no more monthly cycle
              symptoms, and no need for contraception.
            </p>
            <h3 style={h3Style}>How menopause is diagnosed</h3>
            <p style={pStyle}>
              For most women in the typical age range, it's diagnosed clinically — by the
              12-months-without-a-period marker, in the context of age and symptoms. Routine
              hormone testing usually isn't necessary, though it may help when menopause appears
              unusually early.
            </p>
          </section>

          {/* Postmenopause */}
          <section id="postmenopause">
            <h2 style={h2Style}>Stage 4: Postmenopause</h2>
            <h3 style={h3Style}>What it is</h3>
            <p style={pStyle}>
              Postmenopause is everything after you've reached menopause — and it lasts the rest of
              your life. Hormones stay at a steady, low level. For many women the most disruptive
              symptoms gradually ease, though some can linger for years.
            </p>
            <h3 style={h3Style}>What can persist or emerge</h3>
            <p style={pStyle}>
              Hot flashes and night sweats often continue for several years, sometimes longer.
              Vaginal and urinary symptoms frequently don't resolve on their own and may worsen —
              this cluster is called the genitourinary syndrome of menopause (GSM): dryness,
              irritation, discomfort with sex, and urinary symptoms from low estrogen. It's very
              common yet under-recognized, and very treatable.
            </p>
            <h3 style={h3Style}>The long-term health picture</h3>
            <p style={pStyle}>
              This is where the focus shifts from symptom relief to long-term health, because
              sustained low estrogen affects more than comfort:
            </p>
            <ul style={ulStyle}>
              <li>
                Bone health: after menopause, bone loss accelerates — roughly 1–2% per year,
                fastest in the first several years — raising the risk of osteoporosis and
                fractures. Bone density (DEXA) testing is how it's screened.
              </li>
              <li>
                Heart health: as estrogen declines, the risk of high blood pressure, heart attack,
                and stroke rises, and postmenopausal women have notably higher coronary heart
                disease rates than same-age women who haven't reached menopause. Heart disease is
                the leading cause of death in women.
              </li>
              <li>
                Population differences: research, including the large SWAN study (Study of Women's
                Health Across the Nation), shows the timing, symptom burden, and some long-term
                risks of menopause vary across racial and ethnic groups — an important and active
                area of women's health.
              </li>
            </ul>
            <h3 style={h3Style}>Why this stage matters</h3>
            <p style={pStyle}>
              Postmenopause is the long game. Tracking trends over months and years — symptoms plus
              the markers that signal bone, heart, and metabolic risk — turns scattered data points
              into a picture you and your clinician can act on.
            </p>
          </section>

          {/* Treatment */}
          <section id="treatment">
            <h2 style={h2Style}>Treatment &amp; Symptom Management</h2>
            <p style={pStyle}>
              There's a recurring theme across every reputable source: there's no one-size-fits-all
              answer, and you don't have to suffer in silence. The right approach is individualized
              through shared decision-making with your clinician, based on your symptoms, history,
              risk factors, and preferences.
            </p>
            <h3 style={h3Style}>Menopausal hormone therapy (MHT / HRT)</h3>
            <p style={pStyle}>
              The most effective treatment for hot flashes and night sweats, and it helps prevent
              postmenopausal bone loss. What current evidence emphasizes: timing matters — for
              healthy women who start before age 60 or within 10 years of menopause, without
              specific contraindications, benefits generally outweigh risks for bothersome
              symptoms. Route and dose matter — lower doses and transdermal (patch/gel) estrogen
              are often favored, especially with cardiometabolic or clotting risk. It is not
              prescribed to prevent heart disease. It isn't for everyone — contraindications
              include a history of breast cancer, active blood clots, and certain cardiovascular
              conditions. And the guidance is actively evolving: major bodies including The
              Menopause Society have updated their positions, and in 2025 the FDA moved to update
              hormone therapy labeling after reevaluating older data. Current clinician input is
              essential.
            </p>
            <h3 style={h3Style}>Non-hormonal prescription options</h3>
            <p style={pStyle}>
              For women who can't or prefer not to use hormones, effective options exist and the
              category is expanding: fezolinetant (Veozah), FDA-approved in May 2023, the first NK3
              receptor antagonist designed specifically for hot flashes (note: a 2024 FDA warning
              about rare but serious liver injury means liver monitoring is required);
              elinzanetant (Lynkuet), FDA-approved in October 2025, a newer dual NK1/NK3 antagonist
              that also showed sleep benefits; and off-label options including certain
              antidepressants (SSRIs/SNRIs), gabapentin, and oxybutynin.
            </p>
            <h3 style={h3Style}>Treatments for vaginal and urinary symptoms (GSM)</h3>
            <p style={pStyle}>
              Low-dose local vaginal estrogen is highly effective for dryness, discomfort, and
              related urinary symptoms, and delivers very little hormone to the rest of the body.
              Non-hormonal options (such as ospemifene, plus moisturizers and lubricants) also
              help. Because GSM tends not to resolve on its own, treating it is worthwhile.
            </p>
            <h3 style={h3Style}>Lifestyle foundations</h3>
            <p style={pStyle}>
              Not flashy, but genuinely impactful for long-term heart and bone health, not just
              symptoms: movement, especially strength training; a heart- and bone-supportive diet;
              sleep hygiene and stress management; not smoking and moderating alcohol; and
              identifying your personal hot-flash triggers.
            </p>
          </section>

          {/* FAQ */}
          <section id="faq">
            <h2 style={h2Style}>Frequently Asked Questions</h2>
            {faqItems.map((item) => (
              <div key={item.q} style={{ marginBottom: 20 }}>
                <h3 style={h3Style}>{item.q}</h3>
                <p style={pStyle}>{item.a}</p>
              </div>
            ))}
          </section>

          {/* Sources */}
          <section>
            <h2 style={h2Style}>Sources &amp; Medical References</h2>
            <p style={pStyle}>
              This guide is synthesized in our own words from leading evidence-based authorities,
              including The Menopause Society (formerly the North American Menopause Society), Mayo
              Clinic, Cleveland Clinic, the U.S. National Institutes of Health, the U.S. Food &amp;
              Drug Administration, the American College of Obstetricians and Gynecologists (ACOG),
              and the STRAW+10 reproductive staging framework, along with large cohort studies
              such as SWAN.
            </p>
          </section>

          <p
            style={{
              fontFamily: "'Lora', serif",
              fontSize: 13,
              lineHeight: 1.65,
              color: MUTED,
              marginTop: 40,
              padding: "16px 18px",
              background: "rgba(75,31,79,0.04)",
              border: "1px solid rgba(75,31,79,0.08)",
              borderRadius: 10,
            }}
          >
            This page is for general educational purposes only and is not medical advice. It is not
            intended to diagnose, treat, cure, or prevent any condition and is not a substitute for
            professional medical advice, diagnosis, or treatment. Menopause symptoms can overlap
            with other conditions, and individual circumstances vary. Always consult a qualified
            healthcare provider about your health, symptoms, or treatment options, and never
            disregard or delay professional advice because of something you read here. Treatment
            decisions — especially regarding hormone therapy or prescription medications — should
            be made with your clinician based on your personal health history.
          </p>
        </article>
      </main>
    </div>
  );
}
